// app/frontend/hooks/useFileUpload.ts

import { useState, useCallback, useRef } from 'react'
import { DirectUpload } from '@rails/activestorage'
import { isAllowedFileType, isFileSizeAllowed } from '@/lib/fileUpload'

export interface PendingFile {
  file: File
  progress: number
  status: 'uploading' | 'uploaded' | 'error'
  error?: string
  // Network/storage failures can be retried; validation rejections can't.
  retryable?: boolean
  blobSignedId?: string
}

interface StashedFile extends PendingFile {
  token: number
}

export type AttachmentStash = Record<string, StashedFile>

interface InFlightUpload {
  token: number
  promise: Promise<string | null>
  resolve: (signedId: string | null) => void
  xhr?: XMLHttpRequest
}

const DEFAULT_SCOPE = 'default'

/**
 * Applies an upload's progress/result to its stash entry — unless the entry
 * has since been removed or replaced, in which case the stale upload must not
 * resurrect it or overwrite the newer file.
 */
export function patchAttachment(
  stash: AttachmentStash,
  scope: string,
  token: number,
  patch: Partial<PendingFile>
): AttachmentStash {
  const entry = stash[scope]
  if (!entry || entry.token !== token) return stash
  return { ...stash, [scope]: { ...entry, ...patch } }
}

export function removeAttachment(stash: AttachmentStash, scope: string): AttachmentStash {
  if (!(scope in stash)) return stash
  const next = { ...stash }
  delete next[scope]
  return next
}

/**
 * Attachments upload the moment they're picked or pasted, so by the time the
 * agent has typed their reply the file is usually already stored and sending
 * is instant.
 *
 * Pass a scope (the conversation id) to keep each conversation's attachment
 * separate: switching conversations mid-upload leaves the upload running and
 * the file waiting in the conversation it was pasted into.
 */
export function useFileUpload(scopeId: string = DEFAULT_SCOPE) {
  const [stash, setStash] = useState<AttachmentStash>({})
  const uploads = useRef<Record<string, InFlightUpload>>({})
  const nextToken = useRef(0)

  const pendingFile: PendingFile | null = stash[scopeId] ?? null

  // Stops a scope's upload and settles its promise with null. ActiveStorage
  // never calls back for an aborted XHR, so without this a send awaiting the
  // upload would hang forever.
  const abandonUpload = useCallback((scope: string) => {
    const inFlight = uploads.current[scope]
    if (!inFlight) return
    delete uploads.current[scope]
    inFlight.xhr?.abort()
    inFlight.resolve(null)
  }, [])

  const startUpload = useCallback((scope: string, file: File) => {
    const token = ++nextToken.current
    setStash(prev => ({ ...prev, [scope]: { file, progress: 0, status: 'uploading', token } }))

    const inFlight = { token } as InFlightUpload
    inFlight.promise = new Promise((resolve) => {
      inFlight.resolve = resolve
      const delegate = {
        directUploadWillStoreFileWithXHR: (xhr: XMLHttpRequest) => {
          inFlight.xhr = xhr
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100)
              setStash(prev => patchAttachment(prev, scope, token, { progress }))
            }
          })
        }
      }

      new DirectUpload(file, '/rails/active_storage/direct_uploads', delegate).create((error, blob) => {
        if (error || !blob) {
          setStash(prev => patchAttachment(prev, scope, token, {
            status: 'error',
            error: error?.message || 'Upload failed',
            retryable: true,
            progress: 0
          }))
          resolve(null)
        } else {
          setStash(prev => patchAttachment(prev, scope, token, {
            status: 'uploaded',
            progress: 100,
            blobSignedId: blob.signed_id
          }))
          resolve(blob.signed_id)
        }
      })
    })
    uploads.current[scope] = inFlight
  }, [])

  const handleFileSelect = useCallback((file: File) => {
    const scope = scopeId
    abandonUpload(scope)
    const reject = (error: string) => {
      setStash(prev => ({ ...prev, [scope]: { file, progress: 0, status: 'error', error, token: ++nextToken.current } }))
    }

    if (!isAllowedFileType(file.type)) return reject('File type not supported')
    if (!isFileSizeAllowed(file.size)) return reject('File too large (max 10MB)')

    startUpload(scope, file)
  }, [scopeId, abandonUpload, startUpload])

  const retryUpload = useCallback(() => {
    const entry = stash[scopeId]
    if (!entry || entry.status !== 'error' || !entry.retryable) return
    handleFileSelect(entry.file)
  }, [stash, scopeId, handleFileSelect])

  // Resolves with the blob's signed id once the in-flight upload finishes
  // (immediately if it already has), or null if it failed or there's no file.
  const uploadFile = useCallback((scope: string = scopeId): Promise<string | null> => {
    return uploads.current[scope]?.promise ?? Promise.resolve(null)
  }, [scopeId])

  const clearFile = useCallback((scope: string = scopeId) => {
    abandonUpload(scope)
    setStash(prev => removeAttachment(prev, scope))
  }, [scopeId, abandonUpload])

  const handleFileRemove = useCallback(() => clearFile(), [clearFile])

  return {
    pendingFile,
    handleFileSelect,
    handleFileRemove,
    retryUpload,
    uploadFile,
    clearFile
  }
}
